"use client";

import { TypeReviews } from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import {
  faChevronLeft,
  faChevronRight,
  faCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@yardzen/ui/components/button";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@yardzen/ui/components/carousel";
import { H3 } from "@yardzen/ui/components/h3";
import { P } from "@yardzen/ui/components/p";
import { cva } from "class-variance-authority";
import { useRouter } from "next/navigation";

const reviewVariant = cva("", {
  variants: {
    wrapperClass: {
      "side-navigation": "mb-4 flex flex-col gap-4 px-2 py-12 md:py-24",
      default:
        "bg-brand-beige-light mb-4 flex flex-col gap-4 px-8 py-12 md:py-24",
    },
    headerWrapperClass: {
      "side-navigation":
        "max-w-[850px] flex-col items-center gap-2 text-center m-auto",
      default:
        "flex max-w-[850px] flex-col items-center gap-2 text-center md:items-start md:text-start",
    },
    headerClass: {
      "side-navigation": "mb-4 flex flex-col gap-4 px-2 py-12 md:py-24",
      default:
        "bg-brand-beige-light mb-4 flex flex-col gap-4 px-8 py-12 md:py-24",
    },
    carouselClass: {
      "side-navigation": "px-12",
      default: "px-4",
    },
    carouselItemClass: {
      "side-navigation": "basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4",
      default: "basis-full md:basis-1/3 lg:basis-1/4 xl:basis-1/5",
    },
    cardClass: {
      "side-navigation":
        "text-black-elderberry relative flex h-full sm:h-[350px] shrink-0 flex-col justify-between gap-2 rounded bg-[#FAFFF8] p-8 md:p-12 text-center",
      default:
        "text-black-elderberry relative flex h-[180px] min-w-[285px] max-w-[334px] shrink-0 flex-col justify-between gap-2 rounded bg-[#FAFFF8] p-[18px]",
    },
    cardTextClass: {
      "side-navigation": "text-lg text-zinc-800 leading-[140%]",
      default: "text-xs text-zinc-800",
    },
  },
});

export function Reviews({
  reviews,
}: {
  reviews: TypeReviews<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const router = useRouter();
  const reviewsArray = reviews.fields.reviews;
  const primaryCta = reviews.fields.CTAs?.find(
    (cta) => cta?.fields?.variant === "default",
  );
  const variantResolved =
    reviews.fields.variant === "side-navigation"
      ? "side-navigation"
      : "default";

  return (
    <div className={reviewVariant({ wrapperClass: variantResolved })}>
      <div className="flex flex-col justify-center md:flex-row md:justify-between">
        <div className={reviewVariant({ headerWrapperClass: variantResolved })}>
          {variantResolved === "default" && (
            <div className="flex flex-row gap-1 md:items-start">
              <FontAwesomeIcon
                icon={faStar}
                className="fill-[#E19B34] text-xl text-[#E19B34]"
              />
              <FontAwesomeIcon
                icon={faStar}
                className="fill-[#E19B34] text-xl text-[#E19B34]"
              />
              <FontAwesomeIcon
                icon={faStar}
                className="fill-[#E19B34] text-xl text-[#E19B34]"
              />
              <FontAwesomeIcon
                icon={faStar}
                className="fill-[#E19B34] text-xl text-[#E19B34]"
              />
              <FontAwesomeIcon
                icon={faStar}
                className="fill-[#E19B34] text-xl text-[#E19B34]"
              />
            </div>
          )}
          <H3 className="font-display max-w-[850px] text-3xl md:text-5xl">
            {reviews.fields.title}
          </H3>
        </div>
        {primaryCta?.fields.url && (
          <div className="mt-4 flex flex-col justify-center md:mt-0">
            <Button
              variant="default"
              className="mx-auto w-fit rounded-full px-6 py-4"
              onClick={() => {
                const url = primaryCta?.fields.url;
                if (url) {
                  router.push(url);
                }
              }}
              endIcon={<FontAwesomeIcon icon={faArrowRight} />}
            >
              {primaryCta?.fields.buttonText}
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-col overflow-hidden py-4">
        <Carousel
          className={reviewVariant({
            carouselClass: variantResolved,
          })}
          opts={{
            loop: true,
          }}
        >
          <CarouselContent className="pb-6" aria-live="off">
            {reviewsArray.map((review) => {
              return (
                <CarouselItem
                  key={review?.sys.id}
                  className={reviewVariant({
                    carouselItemClass: variantResolved,
                  })}
                >
                  <Card
                    key={review?.sys.id}
                    name={review?.fields.name ?? ""}
                    quote={review?.fields.review ?? ""}
                    location={review?.fields.location ?? ""}
                    packageName={review?.fields.package ?? ""}
                    variantResolved={variantResolved}
                  />
                </CarouselItem>
              );
            })}
          </CarouselContent>
          {variantResolved === "default" && (
            <CarouselDots
              dotClassName="border-brand-secondary-dark h-2.5 w-2.5 rounded-full"
              selectedDotClassName="border-brand-secondary-dark bg-brand-secondary-dark h-2.5 w-2.5 rounded-full"
              count={reviewsArray.length}
            />
          )}
          {variantResolved === "side-navigation" && (
            <>
              <CarouselPrevious
                className="bg-system-grayscale-30 hover:bg-system-grayscale-50 active:bg-system-grayscale-50 left-0 rounded-none border-0 px-5 py-8 [&_svg]:h-6 [&_svg]:w-6 [&_svg]:text-white"
                aria-label="Previous image"
                variant="zest"
                icon={faChevronLeft}
              />
              <CarouselNext
                className="bg-system-grayscale-30 hover:bg-system-grayscale-50 active:bg-system-grayscale-50 right-0 rounded-none border-0 px-5 py-8 [&_svg]:h-6 [&_svg]:w-6 [&_svg]:text-white"
                aria-label="Next image"
                variant="default"
                icon={faChevronRight}
              />
            </>
          )}
        </Carousel>
      </div>
    </div>
  );
}

function Card({
  name,
  quote,
  location,
  packageName,
  variantResolved,
}: {
  image?: React.ReactNode;
  name: string;
  quote: string;
  location: string;
  packageName: string;
  variantResolved: "default" | "side-navigation";
}) {
  return (
    <div
      className={reviewVariant({
        cardClass: variantResolved,
      })}
    >
      <P
        className={reviewVariant({
          cardTextClass: variantResolved,
        })}
      >
        {quote}
      </P>
      <div className="nowrap flex flex-col justify-between gap-1 align-bottom">
        <P className="font-display text-2xl">{name}</P>
        <P className="mt-2 text-xs font-semibold" noSpacing={true}>
          {packageName}
          {location && (
            <>
              {" "}
              <FontAwesomeIcon
                className="mb-[1px] text-[5px]"
                icon={faCircle}
              />{" "}
              {location}
            </>
          )}
        </P>
      </div>
    </div>
  );
}
